<template>
  <div class="shell">

    <!-- Top navbar -->
    <nav class="shell-nav">
      <router-link to="/sites" class="shell-logo" data-testid="nav-home" title="My Websites">
        <img class="shell-logo-icon" :src="brandLogo" alt="Home" />
      </router-link>
      <div class="shell-divider"></div>
      <div class="shell-links">
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/growth-progress`" class="shell-link" active-class="shell-link-active" data-testid="nav-growth-progress">Growth</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/settings`" class="shell-link" active-class="shell-link-active" data-testid="nav-website">Website</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/social`" class="shell-link" active-class="shell-link-active" data-testid="nav-social">Social media</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/capabilities`" class="shell-link" active-class="shell-link-active" data-testid="nav-capabilities">Capabilities</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/campaigns`" class="shell-link" active-class="shell-link-active" data-testid="nav-campaigns">Campaigns</router-link>
      </div>
      <div class="shell-right">
        <!-- Editor actions teleport here (Publish pipeline, Settings, GitHub)
             when the editor route is active; empty and collapsed otherwise. -->
        <div id="editor-nav-slot" class="shell-editor-slot"></div>
        <div class="shell-user-wrap" ref="userMenuRef">
          <button
            type="button"
            class="shell-avatar"
            :title="displayUsername"
            data-testid="user-avatar"
            :aria-expanded="userMenuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="userMenuOpen = !userMenuOpen"
          >{{ userInitials }}</button>
          <div v-if="userMenuOpen" class="shell-user-menu" role="menu" data-testid="user-menu">
            <div class="shell-user-menu-header">
              <div class="shell-user-menu-name">{{ displayUsername }}</div>
              <div v-if="user?.email" class="shell-user-menu-email">{{ user.email }}</div>
              <div class="shell-user-menu-tenant">{{ displayTenant }}</div>
            </div>
            <div class="shell-user-menu-divider"></div>
            <router-link to="/settings" class="shell-user-menu-item" role="menuitem" data-testid="user-menu-settings" @click="userMenuOpen = false">
              <span>Account Settings</span>
            </router-link>
            <router-link to="/sites" class="shell-user-menu-item" role="menuitem" data-testid="user-menu-sites" @click="userMenuOpen = false">
              <span>My Websites</span>
            </router-link>
            <div class="shell-user-menu-divider"></div>
            <button type="button" class="shell-user-menu-item shell-user-menu-danger" role="menuitem" data-testid="user-menu-logout" @click="handleLogout">
              <span>Log out</span>
            </button>
          </div>
        </div>
      </div>
      <button class="shell-hamburger" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Toggle menu">☰</button>

      <!-- Mobile dropdown -->
      <div v-if="mobileMenuOpen" class="shell-mobile-menu">
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/growth-progress`" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-growth-progress" @click="mobileMenuOpen = false">Growth</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/settings`" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-website" @click="mobileMenuOpen = false">Website</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/social`" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-social" @click="mobileMenuOpen = false">Social media</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/capabilities`" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-capabilities" @click="mobileMenuOpen = false">Capabilities</router-link>
        <router-link v-if="currentSiteId" :to="`/sites/${currentSiteId}/campaigns`" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-campaigns" @click="mobileMenuOpen = false">Campaigns</router-link>
        <div class="shell-mobile-sep"></div>
        <router-link to="/sites" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-sites" @click="mobileMenuOpen = false">My Websites</router-link>
        <router-link to="/settings" class="shell-mobile-link" active-class="shell-mobile-link-active" data-testid="nav-mobile-settings" @click="mobileMenuOpen = false">Settings</router-link>
      </div>
    </nav>

    <!-- Main content -->
    <main class="shell-main">
      <router-view />
    </main>

  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import brandLogo from '../assets/bw-logo-wordmark.svg'

export default {
  name: 'AppShell',
  setup() {
    const router     = useRouter()
    const route      = useRoute()
    const sitesStore = useSitesStore()

    const mobileMenuOpen = ref(false)
    const userMenuOpen   = ref(false)
    const userMenuRef    = ref(null)
    watch(() => route.path, () => { mobileMenuOpen.value = false; userMenuOpen.value = false })

    const handleDocumentClick = (event) => {
      if (!userMenuOpen.value) return
      const root = userMenuRef.value
      if (root && !root.contains(event.target)) userMenuOpen.value = false
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape' && userMenuOpen.value) userMenuOpen.value = false
    }
    onMounted(() => {
      document.addEventListener('mousedown', handleDocumentClick)
      document.addEventListener('keydown', handleEscape)
    })
    onUnmounted(() => {
      document.removeEventListener('mousedown', handleDocumentClick)
      document.removeEventListener('keydown', handleEscape)
    })

    const user   = computed(() => sitesStore.user)
    const tenant = computed(() => sitesStore.tenant)

    const displayUsername = computed(() => user.value?.username || 'Account')
    const displayTenant   = computed(() => tenant.value?.name   || 'Workspace')
    const isAdmin         = computed(() => Boolean(user.value?.isAdmin || user.value?.is_admin))
    const currentSiteId   = computed(() => route.params.id || sitesStore.sites[0]?.id || null)

    const userInitials = computed(() => {
      const name = user.value?.username || ''
      return name.slice(0, 2).toUpperCase() || 'AR'
    })

    const handleLogout = () => {
      userMenuOpen.value = false
      sitesStore.clearAuth()
      router.push('/login')
    }

    return { user, tenant, displayUsername, displayTenant, isAdmin, userInitials, handleLogout, mobileMenuOpen, userMenuOpen, userMenuRef, currentSiteId, brandLogo }
  }
}
</script>

<style scoped>
.shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: visible;

  /* Shared menu typography — keep every nav/menu item identical */
  --menu-size: 13px;
  --menu-weight: 500;
  --menu-color: rgba(255,255,255,0.7);
  --menu-color-hover: rgba(255,255,255,0.95);
  --menu-active-bg: rgba(255,255,255,0.12);
}

/* ── Top navbar ── */
.shell-nav {
  height: 52px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  background: rgba(15,15,24,0.90);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255,255,255,0.12);
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
  overflow: visible;
}

.shell-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: rgba(255,255,255,0.92);
  letter-spacing: -0.3px;
  white-space: nowrap;
  text-decoration: none;
}
.shell-logo-icon {
  width: 170px;
  height: 28px;
  border-radius: 0;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.shell-divider {
  width: 1px;
  height: 20px;
  background: rgba(255,255,255,0.1);
  margin: 0 8px;
  flex-shrink: 0;
}

.shell-links {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
}
.shell-link {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: var(--menu-size);
  font-weight: var(--menu-weight);
  color: var(--menu-color);
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.shell-link:hover {
  color: var(--menu-color-hover);
  background: rgba(255,255,255,0.06);
}
.shell-link-active {
  color: var(--menu-color-hover) !important;
  background: var(--menu-active-bg) !important;
}

.shell-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
}
.shell-editor-slot { display: flex; align-items: center; gap: 8px; }
.shell-editor-slot:empty { display: none; }
.shell-btn-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  background: #7c6dfa;
  color: #fff;
  text-decoration: none;
  box-shadow: 0 0 18px rgba(124,109,250,0.35);
  transition: all 0.15s;
  white-space: nowrap;
}
.shell-btn-new:hover {
  background: #6d5ef0;
  box-shadow: 0 0 28px rgba(124,109,250,0.45);
}
.shell-user-wrap {
  position: relative;
}
.shell-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #7c6dfa;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px rgba(124,109,250,0.35);
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.16);
  padding: 0;
  font-family: inherit;
  transition: box-shadow 0.15s, transform 0.15s;
}
.shell-avatar:hover,
.shell-avatar[aria-expanded="true"] {
  box-shadow: 0 0 18px rgba(124,109,250,0.55);
  transform: translateY(-1px);
}
.shell-avatar:focus-visible {
  outline: 2px solid rgba(124,109,250,0.65);
  outline-offset: 2px;
}

.shell-user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 240px;
  background: rgba(22,22,38,0.96);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 12px;
  box-shadow: 0 20px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(124,109,250,0.18);
  padding: 6px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: shell-user-menu-in 0.12s ease-out;
}
@keyframes shell-user-menu-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.shell-user-menu-header {
  padding: 10px 12px 8px;
}
.shell-user-menu-name {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.92);
  line-height: 1.2;
}
.shell-user-menu-email {
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  word-break: break-all;
}
.shell-user-menu-tenant {
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(124,109,250,0.85);
}
.shell-user-menu-divider {
  height: 1px;
  background: rgba(255,255,255,0.10);
  margin: 4px 0;
}
.shell-user-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: var(--menu-size);
  font-weight: var(--menu-weight);
  color: var(--menu-color);
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  width: 100%;
  transition: background 0.12s, color 0.12s;
}
.shell-user-menu-item:hover {
  color: var(--menu-color-hover);
  background: rgba(255,255,255,0.08);
}
.shell-user-menu-danger {
  color: #fca5a5;
}
.shell-user-menu-danger:hover {
  color: #fecaca;
  background: rgba(239,68,68,0.12);
}

/* ── Main content ── */
.shell-main {
  flex: 1;
  overflow-y: auto;
}

/* ── Mobile nav ── */
.shell-nav {
  position: relative;
}

.shell-hamburger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px; height: 32px;
  border-radius: 8px;
  background: none;
  border: var(--glass-border, 1px solid rgba(255,255,255,0.12));
  color: rgba(255,255,255,0.7);
  cursor: pointer;
  margin-left: auto;
  font-size: 18px;
}

.shell-mobile-menu {
  position: absolute;
  top: 52px; left: 0; right: 0;
  background: rgba(15,15,24,0.97);
  backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255,255,255,0.12);
  padding: 12px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 99;
}

.shell-mobile-link {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: var(--menu-size);
  font-weight: var(--menu-weight);
  color: var(--menu-color);
  text-decoration: none;
  transition: all 0.15s;
}
.shell-mobile-link:hover,
.shell-mobile-link-active { color: var(--menu-color-hover); background: var(--menu-active-bg); }
.shell-mobile-sep { height: 1px; background: rgba(255,255,255,0.1); margin: 6px 4px; }

@media (max-width: 767px) {
  .shell-links { display: none; }
  .shell-btn-new { display: none; }
  .shell-hamburger { display: flex; }
}
</style>
